import React, { useContext, useState } from 'react'
import { useAuth } from '../../providers/useAuth'
import { SocialButtons } from './SocialButtons'
import { FormPageWrapper } from './FormPageWrapper'
import { FooterText } from './FooterText'
import { FormMessage } from './FormMessage'
import { LinkContext } from '../../providers/LinkProvider'
import { AuthPageContext } from '../../providers/AuthPageProvider'
import { AppState } from 'common/state-type'
import { encodeAuthState } from 'common/utils/auth'
import VerifyMFAForm from './VerifyMfaForm'
import { CheckboxGroup } from '../Inputs/CheckboxGroup'
import { useForm } from 'react-hook-form'
import { Button } from 'common/components/Authentication/Button'
import { InputField, PasswordField } from 'cwp-components/forms'

type LoginPageProps = {
  onSuccess?: (data: any) => void
}

const LoginPage = ({ onSuccess }: LoginPageProps) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    setError: setFieldError,
    trigger,
  } = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange',
  })
  const [isChecked, setIsChecked] = useState<boolean>(false)

  const handleChange = () => {
    setIsChecked(!isChecked)
  }

  const onSubmit = async (values) => {
    const fieldNames = Object.keys(errors)
    await trigger(fieldNames)
    login(
      {
        username: values.emailAddress,
        password: values.password,
      },
      {
        onSuccess: (cognitoUser: AppState['user']) => {
          if (cognitoUser?.challengeName === 'NEW_PASSWORD_REQUIRED') {
            document.location = `${
              document.location.origin
            }/reset-password?email=${encodeURIComponent(values.email)}`
            return
          }

          if (cognitoUser?.challengeName === 'SOFTWARE_TOKEN_MFA') {
            setRequiresMfa(true)
            return
          }
          onSuccess?.(cognitoUser)
        },
        onError: (e) => {
          if (e?.includes('not confirmed')) {
            document.location = `${
              document.location.origin
            }/verify-email?state=${encodeAuthState({
              email: values.emailAddress,
              password: values.password,
            })}`
            return
          }
          // MFA verification required
          if (e?.includes('not authenticated')) {
            return
          }
          if (e === 'Incorrect username or password.') {
            setFieldError('password', {
              message: 'The password you’ve entered is incorrect',
            })
          } else if (e === 'User does not exist.') {
            setFieldError('emailAddress', {
              message: 'Please enter an email connected to an account',
            })
          } else {
            setError(e)
          }
        },
      },
    )
  }

  const { hasSocialSignIn } = useContext(AuthPageContext)
  const { login, userLoading } = useAuth()
  const [userError, setError] = useState<boolean>(false)
  const [requiresMfa, setRequiresMfa] = useState<boolean>(false)
  const { Link } = useContext(LinkContext)

  if (requiresMfa) {
    return (
      <FormPageWrapper title='Two-Factor Authentication'>
        <div className='flex flex-col items-center'>
          <VerifyMFAForm onSuccess={API.loginRedirect} />
        </div>
      </FormPageWrapper>
    )
  }
  const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/

  return (
    <FormPageWrapper className={'w-full md:w-[84%] '} title='Sign in'>
      <FormMessage type='error'>{userError}</FormMessage>
      <form onSubmit={handleSubmit(onSubmit)} className='mt-10'>
        <div className='grid md:grid-cols-3 gap-4'>
          <div className='flex flex-col col-span-3 item-center'>
            <InputField
              register={register}
              required={true}
              regexPattern={emailPattern}
              id='emailAddress'
              label='Email address'
              placeholder='Email address'
              errors={errors}
              errorMessage='Please enter an email connected to an account'
            />
          </div>
          <div className='flex flex-col col-span-3 item-center'>
            <PasswordField
              register={register}
              required={true}
              id='password'
              label='Password'
              placeholder='password'
              isLabelVisible
              errors={errors}
              errorMessage='Please enter the password'
            />
          </div>
          <div className='mb-6 col-span-3 w-full flex justify-between items-center'>
            <CheckboxGroup
              id='rmrPasswords'
              checked={isChecked}
              label={
                <span className='text-right text-[#6E7785] text-sm'>
                  Remember my password
                </span>
              }
              name='rmrPasswords'
              onChange={handleChange}
            />
            <div className='text-right'>
              <Link
                href='/forgot-password'
                className='cursor pointer text-[#5858DF]'
              >
                <span className='text-sm'> Forgotten password</span>
              </Link>
            </div>
          </div>
        </div>

        <div className='flex justify-center mt-6'>
          <Button
            type='submit'
            className={'!w-[25%]  !mx-auto'}
            disabled={Object.keys(errors)?.length > 0}
          >
            Continue
          </Button>
        </div>
      </form>
      <div className='mt-4'>
        {hasSocialSignIn && (
          <>
            <p className='text-base font-normal text-center text-primary-font !py-4 mb-4 !leading-6'>
              or
            </p>
            <SocialButtons className={'w-full mx-auto'} action='Sign in' />
          </>
        )}
        <FooterText
          text="Don't have an account?"
          linkHref='/signup'
          linkText='Sign up'
        />
      </div>
    </FormPageWrapper>
  )
}

export default LoginPage
